import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { money, number, thaiDate } from '../lib/format'

/**
 * LLM response cache (part 42).
 *
 * The page is a window on server-owned state: prices, TTL and budget live in the API and the
 * database, never in this component. The "test twice" panel deliberately sends two separate
 * requests with different Idempotency-Keys. If it reused one key the second answer would be an
 * idempotency replay of the first (cache_hit=false), which would look like a miss and prove
 * nothing. Two keys force the second request through the real cache lookup.
 */

interface LLMModel {
  model: string
  prompt_satang_per_1k: number
  completion_satang_per_1k: number
  source: 'default' | 'tenant'
  updated_by?: string
  updated_at?: string
}

interface LLMModelsResponse {
  models: LLMModel[]
  currency: string
  unit: string
  default_ttl_seconds: number
  daily_budget_satang: number
  provider: 'stub' | 'openai'
}

interface CacheStats {
  entries: number
  active_entries: number
  expired_entries: number
  total_hits: number
  calls_30d: number
  hits_30d: number
  misses_30d: number
  hit_rate_pct: number
  tokens_saved: number
  cost_saved_satang: number
  spend_today_satang: number
}

interface StatsResponse {
  stats: CacheStats
  checked_at: string
  timezone: string
  daily_budget_satang: number
  default_ttl_seconds: number
}

interface UsageDay {
  day: string
  calls: number
  hits: number
  misses: number
  prompt_tokens: number
  completion_tokens: number
  cost_satang: number
  saved_satang: number
}

interface UsageResponse {
  days: UsageDay[]
  from: string
  to: string
  timezone: string
  totals: UsageDay
}

interface LLMResult {
  text: string
  model: string
  cache_key: string
  cache_hit: boolean
  prompt_tokens: number
  completion_tokens: number
  cost_satang: number
  saved_satang: number
  prompt_tokens_saved: number
  completion_tokens_saved: number
  expires_at: string
  over_budget: boolean
}

interface CompleteResponse {
  result: LLMResult
  provider: 'stub' | 'openai'
  daily_budget_satang: number
  ttl_seconds: number
}

const MODELS_KEY = ['llm-models'] as const
const STATS_KEY = ['llm-cache-stats'] as const
const USAGE_KEY = ['llm-usage-by-day'] as const

const DEFAULT_PROMPT = 'สรุปความเสี่ยงจากการจ่ายซ้ำในโครงการตรวจสอบงวดล่าสุด โดยอ้างอิงหลักฐานที่มีอยู่'

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function parsePrice(raw: string): number | null {
  const trimmed = raw.trim()
  if (!/^\d+$/.test(trimmed)) return null
  const value = Number(trimmed)
  return Number.isSafeInteger(value) && value >= 0 && value <= 10_000_000 ? value : null
}

function sourceLabel(source: LLMModel['source']): string {
  return source === 'tenant' ? 'ราคาขององค์กร' : 'ค่าเริ่มต้นจากระบบ'
}

function UsageChart({ days }: { days: UsageDay[] }) {
  if (days.length === 0) return null
  const W = 900
  const H = 170
  const left = 52
  const right = 12
  const top = 12
  const bottom = 30
  const plotW = W - left - right
  const plotH = H - top - bottom
  const max = Math.max(1, ...days.map((d) => Math.max(d.cost_satang, d.saved_satang)))
  const slot = plotW / days.length
  const barW = Math.max(2, slot * 0.28)

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="ต้นทุนจริงและต้นทุนที่ประหยัดได้รายวัน" style={{ width: '100%', height: 'auto' }}>
        {[0, 0.5, 1].map((share) => {
          const y = top + plotH * (1 - share)
          return (
            <g key={share}>
              <line x1={left} y1={y} x2={W - right} y2={y} stroke="var(--color-hairline)" strokeDasharray="3 5" />
              <text x={left - 8} y={y + 4} textAnchor="end" fontSize={10} fill="var(--color-ivory-dim)">
                {money(Math.round(max * share), { compact: true })}
              </text>
            </g>
          )
        })}
        {days.map((day, i) => {
          const x = left + slot * i + slot / 2
          const costH = (day.cost_satang / max) * plotH
          const savedH = (day.saved_satang / max) * plotH
          return (
            <g key={day.day}>
              <rect
                x={x - barW - 1}
                y={top + plotH - costH}
                width={barW}
                height={Math.max(0, costH)}
                fill="var(--color-gold)"
                opacity={0.85}
              >
                <title>{`${thaiDate(day.day)}: ใช้จ่าย ${money(day.cost_satang)}`}</title>
              </rect>
              <rect
                x={x + 1}
                y={top + plotH - savedH}
                width={barW}
                height={Math.max(0, savedH)}
                fill="var(--color-emerald-soft)"
                opacity={0.75}
              >
                <title>{`${thaiDate(day.day)}: ประหยัด ${money(day.saved_satang)}`}</title>
              </rect>
              {i % 5 === 0 || i === days.length - 1 ? (
                <text x={x} y={H - 10} textAnchor="middle" fontSize={9.5} fill="var(--color-ivory-dim)">
                  {day.day.slice(5)}
                </text>
              ) : null}
            </g>
          )
        })}
      </svg>
      <div className="row" style={{ gap: 18, marginTop: 8, fontSize: 11.5 }}>
        <span className="row" style={{ gap: 6 }}>
          <i style={{ width: 12, height: 8, background: 'var(--color-gold)', display: 'inline-block' }} />
          <span className="dim">ต้นทุนที่จ่ายจริง</span>
        </span>
        <span className="row" style={{ gap: 6 }}>
          <i style={{ width: 12, height: 8, background: 'var(--color-emerald-soft)', display: 'inline-block' }} />
          <span className="dim">ต้นทุนที่แคชประหยัดได้</span>
        </span>
      </div>
    </div>
  )
}

export function LLMCache() {
  const qc = useQueryClient()
  const [draft, setDraft] = useState<Record<string, { prompt: string; completion: string }>>({})
  const [notice, setNotice] = useState<string | null>(null)
  const [prompt, setPrompt] = useState(DEFAULT_PROMPT)
  const [model, setModel] = useState('')
  const [temperature, setTemperature] = useState('0.2')
  const [ttlSeconds, setTTLSeconds] = useState('86400')
  const [probe, setProbe] = useState<{ first: CompleteResponse; second: CompleteResponse } | null>(null)

  const models = useQuery({
    queryKey: MODELS_KEY,
    queryFn: () => api<LLMModelsResponse>('/v1/llm/models'),
    staleTime: 60_000,
  })
  const stats = useQuery({
    queryKey: STATS_KEY,
    queryFn: () => api<StatsResponse>('/v1/llm/cache/stats'),
    staleTime: 15_000,
  })
  const usage = useQuery({
    queryKey: USAGE_KEY,
    queryFn: () => api<UsageResponse>('/v1/llm/usage/by-day?days=30'),
    staleTime: 30_000,
  })

  const rows = models.data?.models ?? []
  const selectedModel = model || rows[0]?.model || ''
  const s = stats.data?.stats

  const savePrice = useMutation({
    mutationFn: (input: { model: string; prompt: number; completion: number }) =>
      apiWrite<{ model: LLMModel }>(
        `/v1/llm/models/${encodeURIComponent(input.model)}/prices`,
        'PUT',
        {
          prompt_satang_per_1k: input.prompt,
          completion_satang_per_1k: input.completion,
        },
      ),
    onSuccess: (res) => {
      setDraft((old) => {
        const next = { ...old }
        delete next[res.model.model]
        return next
      })
      setNotice(`บันทึกราคาของ ${res.model.model} แล้ว ราคาใหม่มีผลกับการเรียกครั้งถัดไป`)
      void qc.invalidateQueries({ queryKey: MODELS_KEY })
      void qc.invalidateQueries({ queryKey: STATS_KEY })
    },
  })

  const purge = useMutation({
    mutationFn: () => apiWrite<{ purged_entries: number }>('/v1/llm/cache/purge', 'POST', {}),
    onSuccess: (res) => {
      setNotice(`ลบรายการแคชที่หมดอายุ ${number(res.purged_entries)} รายการแล้ว รายการที่ยังไม่หมดอายุไม่ถูกแตะต้อง`)
      void qc.invalidateQueries({ queryKey: STATS_KEY })
      void qc.invalidateQueries({ queryKey: USAGE_KEY })
    },
  })

  const testTwice = useMutation({
    mutationFn: async () => {
      const temperatureValue = Number(temperature)
      if (!Number.isFinite(temperatureValue) || temperatureValue < 0 || temperatureValue > 2) {
        throw new Error('temperature ต้องอยู่ระหว่าง 0 ถึง 2')
      }
      const body = {
        model: selectedModel,
        prompt,
        params: { temperature: temperatureValue },
        ttl_seconds: Number(ttlSeconds),
      }
      // Two separate Idempotency-Keys: apiWrite generates a fresh one per call. The second
      // request is therefore a genuine cache lookup, not an idempotency replay.
      const first = await apiWrite<CompleteResponse>('/v1/llm/complete', 'POST', body)
      const second = await apiWrite<CompleteResponse>('/v1/llm/complete', 'POST', body)
      return { first, second }
    },
    onSuccess: (res) => {
      setProbe(res)
      setNotice(
        res.second.result.cache_hit
          ? 'การเรียกครั้งที่สองเป็นแคชฮิต ไม่มีโทเคนจากผู้ให้บริการและต้นทุนเป็นศูนย์'
          : 'การเรียกครั้งที่สองยังไม่เป็นแคชฮิต ตรวจสอบว่าโมเดล ข้อความ และพารามิเตอร์เหมือนกันทุกค่า',
      )
      void qc.invalidateQueries({ queryKey: STATS_KEY })
      void qc.invalidateQueries({ queryKey: USAGE_KEY })
    },
  })

  const totals = usage.data?.totals
  const visibleDays = useMemo(() => usage.data?.days ?? [], [usage.data])
  const busy = testTwice.isPending || purge.isPending

  return (
    <>
      <PageHead
        title="แคชคำตอบ LLM"
        sub="คีย์แคชสร้างจากโมเดล ข้อความที่ปรับช่องว่างแล้ว และพารามิเตอร์แบบ canonical · TTL ต่อรายการ · ต้นทุนคิดเป็นสตางค์ต่อ 1,000 โทเคน แบบปัดขึ้น"
        actions={
          <div className="row" style={{ gap: 8 }}>
            <button className="btn btn-sm" type="button" onClick={() => { setNotice(null); purge.mutate() }} disabled={busy}>
              {purge.isPending ? 'กำลังลบ…' : 'ลบแคชที่หมดอายุ'}
            </button>
            <button
              className="btn btn-sm"
              type="button"
              onClick={() => {
                void qc.invalidateQueries({ queryKey: MODELS_KEY })
                void qc.invalidateQueries({ queryKey: STATS_KEY })
                void qc.invalidateQueries({ queryKey: USAGE_KEY })
              }}
            >
              รีเฟรช
            </button>
          </div>
        }
      />

      {notice ? (
        <div
          role="status"
          style={{
            marginTop: 4,
            marginBottom: 16,
            padding: '10px 14px',
            fontSize: 12.5,
            border: '1px solid var(--color-emerald-soft)',
            borderRadius: 6,
            color: 'var(--color-emerald-soft)',
          }}
        >
          {notice}
        </div>
      ) : null}

      {purge.isError ? <div className="form-error" style={{ marginBottom: 16 }}>{errorText(purge.error, 'ลบแคชไม่สำเร็จ')}</div> : null}

      {stats.isLoading || models.isLoading ? (
        <div className="stack"><Skel h={110} /><Skel h={180} /><Skel h={220} /></div>
      ) : stats.isError || models.isError ? (
        <div className="card card-pad">
          <div className="form-error">
            {errorText(stats.error ?? models.error, 'โหลดข้อมูลแคชไม่สำเร็จ')}
          </div>
          <p className="dim" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
            ตรวจสอบว่าเส้นทาง /v1/llm/cache/stats และ /v1/llm/models ถูก mount แล้ว
          </p>
        </div>
      ) : (
        <>
          <div className="grid-4 stagger">
            <Stat
              label="รายการในแคช"
              value={number(s?.entries ?? 0)}
              foot={`ใช้งาน ${number(s?.active_entries ?? 0)} · หมดอายุ ${number(s?.expired_entries ?? 0)}`}
            />
            <Stat
              label="อัตราการใช้แคช 30 วัน"
              value={(s?.hit_rate_pct ?? 0).toFixed(1)}
              unit="%"
              foot={`${number(s?.hits_30d ?? 0)} ฮิต จาก ${number(s?.calls_30d ?? 0)} ครั้ง · พลาด ${number(s?.misses_30d ?? 0)}`}
            />
            <Stat
              label="ต้นทุนที่ประหยัดได้"
              value={money(s?.cost_saved_satang ?? 0)}
              foot={`${number(s?.tokens_saved ?? 0)} โทเคนที่ไม่ต้องส่งให้ผู้ให้บริการ`}
            />
            <Stat
              label="ใช้จ่ายวันนี้"
              value={money(s?.spend_today_satang ?? 0)}
              foot={`งบรายวัน ${money(stats.data?.daily_budget_satang ?? 0)} · รีเซ็ตเที่ยงคืน ${stats.data?.timezone ?? ''}`}
            />
          </div>

          <div className="card card-pad" style={{ marginTop: 16 }}>
            <div className="row" style={{ alignItems: 'baseline', marginBottom: 10 }}>
              <span className="card-label">ทดสอบแคชด้วยการเรียกสองครั้ง</span>
              <span className="chip" style={{ marginLeft: 'auto' }}>
                {models.data?.provider === 'stub' ? 'ผู้ให้บริการจำลอง (ไม่ต่อเครือข่าย)' : 'ผู้ให้บริการภายนอก'}
              </span>
            </div>
            <p className="dim" style={{ fontSize: 12, marginTop: 0 }}>
              ระบบส่งคำขอเดิมสองครั้งด้วย Idempotency-Key คนละค่า ครั้งแรกต้องเป็น MISS และครั้งที่สองต้องเป็น HIT
              โดยมีโทเคนผู้ให้บริการเป็นศูนย์และต้นทุนเป็นศูนย์
            </p>

            <div className="grid-2" style={{ gap: 12 }}>
              <div className="field">
                <label htmlFor="llm-model">โมเดล</label>
                <select
                  id="llm-model"
                  className="select"
                  value={selectedModel}
                  onChange={(e) => setModel(e.target.value)}
                  disabled={busy}
                >
                  {rows.map((row) => (
                    <option key={row.model} value={row.model}>{row.model}</option>
                  ))}
                </select>
              </div>
              <div className="row" style={{ gap: 10 }}>
                <div className="field" style={{ flex: 1 }}>
                  <label htmlFor="llm-temperature">temperature</label>
                  <input
                    id="llm-temperature"
                    className="input tnum"
                    value={temperature}
                    onChange={(e) => setTemperature(e.target.value)}
                    disabled={busy}
                  />
                </div>
                <div className="field" style={{ flex: 1 }}>
                  <label htmlFor="llm-ttl">อายุแคช</label>
                  <select
                    id="llm-ttl"
                    className="select"
                    value={ttlSeconds}
                    onChange={(e) => setTTLSeconds(e.target.value)}
                    disabled={busy}
                  >
                    <option value="3600">1 ชั่วโมง</option>
                    <option value="86400">24 ชั่วโมง</option>
                    <option value="604800">7 วัน</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="field">
              <label htmlFor="llm-prompt">ข้อความที่ส่งให้โมเดล</label>
              <textarea
                id="llm-prompt"
                className="input"
                rows={4}
                maxLength={32000}
                style={{ width: '100%', height: 'auto', padding: '10px 12px' }}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                disabled={busy}
              />
            </div>

            <div className="row" style={{ gap: 10 }}>
              <button
                className="btn btn-primary"
                type="button"
                disabled={busy || !selectedModel || prompt.trim() === ''}
                onClick={() => { setNotice(null); setProbe(null); testTwice.mutate() }}
              >
                {testTwice.isPending ? 'กำลังเรียกสองครั้ง…' : 'ทดสอบเรียกสองครั้ง'}
              </button>
              <span className="dim" style={{ fontSize: 11.5 }}>
                ต้นทุนต่อครั้ง = ปัดขึ้น(โทเคนเข้า ÷ 1,000 × ราคาเข้า) + ปัดขึ้น(โทเคนออก ÷ 1,000 × ราคาออก)
              </span>
            </div>

            {testTwice.isError ? (
              <div className="form-error" style={{ marginTop: 12 }}>{errorText(testTwice.error, 'ทดสอบแคชไม่สำเร็จ')}</div>
            ) : null}

            {probe ? (
              <div className="table-wrap" tabIndex={0} role="region" aria-label="ตารางแคชคำตอบ" style={{ marginTop: 14 }}>
                <table className="table data">
                  <thead>
                    <tr>
                      <th>ครั้งที่</th>
                      <th>ผลแคช</th>
                      <th>โทเคนเข้า</th>
                      <th>โทเคนออก</th>
                      <th>ต้นทุน</th>
                      <th>ประหยัดได้</th>
                      <th>คีย์แคช</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[probe.first, probe.second].map((call, i) => (
                      <tr key={i}>
                        <td className="tnum">{i + 1}</td>
                        <td>
                          <span className="chip" style={{ color: call.result.cache_hit ? 'var(--color-emerald)' : 'var(--color-amber)' }}>
                            {call.result.cache_hit ? 'HIT' : 'MISS'}
                          </span>
                        </td>
                        <td className="tnum">{number(call.result.prompt_tokens)}</td>
                        <td className="tnum">{number(call.result.completion_tokens)}</td>
                        <td className="tnum">{money(call.result.cost_satang)}</td>
                        <td className="tnum">{money(call.result.saved_satang)}</td>
                        <td className="dim" style={{ fontFamily: 'monospace', fontSize: 11 }}>{call.result.cache_key.slice(0, 12)}…</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>

          <div className="card card-pad" style={{ marginTop: 16 }}>
            <div className="row" style={{ alignItems: 'baseline', marginBottom: 6 }}>
              <span className="card-label">ราคาโมเดลต่อ 1,000 โทเคน (สตางค์)</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>ราคาขององค์กรมีผลเหนือค่าเริ่มต้นทันที</span>
            </div>
            <div className="table-wrap" tabIndex={0} role="region" aria-label="ตารางแคชคำตอบ">
              <table className="table data">
                <thead>
                  <tr>
                    <th>โมเดล</th>
                    <th>แหล่งราคา</th>
                    <th>โทเคนเข้า (สตางค์/1K)</th>
                    <th>โทเคนออก (สตางค์/1K)</th>
                    <th>แก้ไขล่าสุด</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const d = draft[row.model] ?? {
                      prompt: String(row.prompt_satang_per_1k),
                      completion: String(row.completion_satang_per_1k),
                    }
                    const promptValue = parsePrice(d.prompt)
                    const completionValue = parsePrice(d.completion)
                    const valid = promptValue !== null && completionValue !== null
                    const dirty = d.prompt !== String(row.prompt_satang_per_1k) || d.completion !== String(row.completion_satang_per_1k)
                    return (
                      <tr key={row.model}>
                        <td style={{ fontFamily: 'monospace' }}>{row.model}</td>
                        <td>
                          <span className="chip" style={{ color: row.source === 'tenant' ? 'var(--color-gold)' : undefined }}>
                            {sourceLabel(row.source)}
                          </span>
                        </td>
                        <td>
                          <input
                            className="input tnum"
                            style={{ width: 130 }}
                            value={d.prompt}
                            aria-label={`ราคาโทเคนเข้าของ ${row.model}`}
                            onChange={(e) => setDraft((old) => ({
                              ...old,
                              [row.model]: { ...d, prompt: e.target.value },
                            }))}
                          />
                        </td>
                        <td>
                          <input
                            className="input tnum"
                            style={{ width: 130 }}
                            value={d.completion}
                            aria-label={`ราคาโทเคนออกของ ${row.model}`}
                            onChange={(e) => setDraft((old) => ({
                              ...old,
                              [row.model]: { ...d, completion: e.target.value },
                            }))}
                          />
                        </td>
                        <td className="dim">{row.updated_at ? thaiDate(row.updated_at) : '—'}</td>
                        <td>
                          <button
                            className="btn btn-sm"
                            type="button"
                            disabled={!valid || !dirty || savePrice.isPending}
                            onClick={() => {
                              if (promptValue === null || completionValue === null) return
                              setNotice(null)
                              savePrice.mutate({ model: row.model, prompt: promptValue, completion: completionValue })
                            }}
                          >
                            บันทึก
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {savePrice.isError ? <div className="form-error" style={{ marginTop: 12 }}>{errorText(savePrice.error, 'บันทึกราคาไม่สำเร็จ')}</div> : null}
            <p className="dim" style={{ fontSize: 11.5, marginBottom: 0 }}>
              ราคาเป็นจำนวนเต็มสตางค์ ระบบไม่รับหน่วยบาทเพื่อหลีกเลี่ยงการปัดเศษซ้ำ · ช่วงที่รับคือ 0 ถึง 10,000,000 สตางค์ต่อ 1,000 โทเคน
            </p>
          </div>

          <div className="card card-pad" style={{ marginTop: 16 }}>
            <div className="row" style={{ alignItems: 'baseline', marginBottom: 10 }}>
              <span className="card-label">ต้นทุนและการใช้แคชรายวัน</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                {usage.data ? `${usage.data.from} ถึง ${usage.data.to} · ${usage.data.timezone}` : ''}
              </span>
            </div>
            {usage.isLoading ? (
              <Skel h={190} />
            ) : usage.isError ? (
              <div className="form-error">{errorText(usage.error, 'โหลดข้อมูลการใช้รายวันไม่สำเร็จ')}</div>
            ) : visibleDays.length === 0 ? (
              <Empty title="ยังไม่มีการเรียกโมเดล" body="ทดสอบเรียกสองครั้งด้านบน แล้วกราฟและตารางจะแสดงต้นทุนจริงกับต้นทุนที่แคชประหยัดได้" />
            ) : (
              <>
                <UsageChart days={visibleDays} />
                <div className="table-wrap" style={{ marginTop: 14, maxHeight: 360, overflowY: 'auto' }} tabIndex={0} role="region" aria-label="ตารางแคชคำตอบ">
                  <table className="table data">
                    <thead>
                      <tr>
                        <th>วัน</th>
                        <th>เรียก</th>
                        <th>ฮิต</th>
                        <th>พลาด</th>
                        <th>โทเคนเข้า</th>
                        <th>โทเคนออก</th>
                        <th>ต้นทุนจริง</th>
                        <th>ประหยัดได้</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...visibleDays].reverse().map((day) => (
                        <tr key={day.day}>
                          <td>{thaiDate(day.day)}</td>
                          <td className="tnum">{number(day.calls)}</td>
                          <td className="tnum">{number(day.hits)}</td>
                          <td className="tnum">{number(day.misses)}</td>
                          <td className="tnum">{number(day.prompt_tokens)}</td>
                          <td className="tnum">{number(day.completion_tokens)}</td>
                          <td className="tnum">{money(day.cost_satang)}</td>
                          <td className="tnum">{money(day.saved_satang)}</td>
                        </tr>
                      ))}
                    </tbody>
                    {totals ? (
                      <tfoot>
                        <tr>
                          <td>รวม</td>
                          <td className="tnum">{number(totals.calls)}</td>
                          <td className="tnum">{number(totals.hits)}</td>
                          <td className="tnum">{number(totals.misses)}</td>
                          <td className="tnum">{number(totals.prompt_tokens)}</td>
                          <td className="tnum">{number(totals.completion_tokens)}</td>
                          <td className="tnum">{money(totals.cost_satang)}</td>
                          <td className="tnum">{money(totals.saved_satang)}</td>
                        </tr>
                      </tfoot>
                    ) : null}
                  </table>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </>
  )
}
